import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
  Badge,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import EnvironmentBadge from '../common/EnvironmentBadge';

// --- Types ---
export interface TabsData {
  section: string;
  items: {
    label: string;
    icon?: React.ReactNode;
    route?: string;
    subNav?: { label: string; route: string }[];
    count?: number; // Optional count badge
  }[];
}

interface LeftNavigationProps {
  handleParentClick: (_mainIdx: number, _subIdx: number | null, _route: string) => void;
  activeTab: [number, number];
  activeSubTab: number;
  tabsData: TabsData[];
  currentPath: string;
}

const LeftNavigation: React.FC<LeftNavigationProps> = ({
  handleParentClick,
  activeTab,
  activeSubTab,
  tabsData,
  currentPath,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  const toggleMenu = (key: string) => {
    setOpenMenus((prev) => ({
      [key]: !prev[key],
    }));
  };

  // Key of the submenu that contains the current route (if any)
  const activeSubMenuKey = useMemo(() => {
    for (let mainIdx = 0; mainIdx < tabsData.length; mainIdx++) {
      const itemIdx = tabsData[mainIdx].items.findIndex((item) =>
        item.subNav?.some((sub) => sub.route === currentPath),
      );
      if (itemIdx !== -1) return `${mainIdx}-${itemIdx}`;
    }
    return null;
  }, [tabsData, currentPath]);

  // Expand the submenu of the current route so the active item is visible (e.g. after a refresh)
  useEffect(() => {
    if (activeSubMenuKey) {
      setOpenMenus({ [activeSubMenuKey]: true });
    }
  }, [activeSubMenuKey]);

  // Let the browser handle modifier clicks (open in new tab/window)
  const isModifiedClick = (e: React.MouseEvent) => e.ctrlKey || e.metaKey || e.shiftKey;

  return (
    <Box sx={{ p: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 } }}>
      <Stack
        direction="row"
        spacing={{ xs: 0.75, lg: 1, xl: 1.5 }}
        alignItems="center"
        sx={{
          pl: { xs: '4px', lg: '6px', xl: '8px' },
          height: { xs: '36px', lg: '40px', xl: '44px' },
          mb: { xs: 0.5, lg: 0.75, xl: 1 },
        }}
      >
        <Typography
          sx={{
            fontWeight: '600',
            fontSize: {
              xs: '16px',
              sm: '18px',
              md: '20px',
              lg: '22px',
              xl: '24px',
            },
            color: theme.palette.leftNavigation.title,
          }}
        >
          {t('lubricants.title')}
        </Typography>
        <EnvironmentBadge />
      </Stack>
      {tabsData.map((section, mainIdx) => (
        <Box key={section.section} sx={{ mb: { xs: 1, lg: 1.5, xl: 2 } }}>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{
              px: 1,
              fontSize: { xs: '0.65rem', lg: '0.7rem', xl: '0.75rem' },
            }}
          >
            {section.section}
          </Typography>
          <List disablePadding>
            {section.items.map((item, itemIdx) => {
              const key = `${mainIdx}-${itemIdx}`;
              const isExpanded = openMenus[key] || false;
              const isActive = activeTab[0] === mainIdx && activeTab[1] === itemIdx && !isExpanded;

              // Check if user is already on this route
              const isOnCurrentRoute = item.route === currentPath;

              return (
                <React.Fragment key={item.label}>
                  <ListItemButton
                    selected={isActive}
                    {...(item.route && !item.subNav ? { component: 'a', href: item.route } : {})}
                    onClick={(e: React.MouseEvent) => {
                      if (item.route && !item.subNav && isModifiedClick(e)) return;
                      e.preventDefault();
                      if (isOnCurrentRoute) return; // Prevent navigation to current route
                      if (item.subNav) {
                        toggleMenu(key);
                      } else if (item.route) {
                        handleParentClick(mainIdx, itemIdx, item.route);
                      }
                    }}
                    sx={{
                      '&:hover': {
                        borderRadius: `${theme.borderRadius.full}px`,
                      },
                      '&.Mui-selected': {
                        backgroundColor: 'transparent',
                      },

                      transition: theme.transitions.custom.drawer,
                      borderRadius: isActive ? `${theme.borderRadius.full}px` : undefined,
                      color: isActive
                        ? theme.palette.leftNavigation.activeText
                        : theme.palette.leftNavigation.text,
                      backgroundColor: isActive
                        ? `${theme.palette.leftNavigation.activeBg} !important`
                        : '',
                    }}
                  >
                    {item.icon && (
                      <ListItemIcon
                        sx={{
                          minWidth: { xs: '28px', lg: '30px', xl: '35px' },
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                    )}
                    <ListItemText
                      slotProps={{
                        primary: {
                          sx: {
                            fontSize: { xs: '12px', lg: '13px', xl: '14px' },
                          },
                        },
                      }}
                      primary={item.label}
                    />
                    {/* Count Badge */}
                    {item.count !== undefined && item.count > 0 && (
                      <Badge
                        badgeContent={item.count}
                        color="error"
                        invisible={!item.count || item.count <= 0}
                        sx={{
                          '& .MuiBadge-badge': {
                            fontSize: {
                              xs: '10px',
                              md: '12px',
                              lg: '11px',
                              xl: '12px',
                            },
                            minWidth: {
                              xs: '20px',
                              md: '24px',
                              lg: '22px',
                              xl: '26px',
                            },
                            height: {
                              xs: '20px',
                              md: '24px',
                              lg: '22px',
                              xl: '26px',
                            },
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0',
                          },
                        }}
                      />
                    )}
                    {item.subNav &&
                      (isExpanded ? (
                        <ExpandLess sx={{ fontSize: { xs: 15, lg: 16, xl: 17 } }} />
                      ) : (
                        <ExpandMore sx={{ fontSize: { xs: 15, lg: 16, xl: 17 } }} />
                      ))}
                  </ListItemButton>

                  {/* Collapsible SubNav */}
                  {item.subNav && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.subNav.map((sub, subIdx) => {
                          const subIsActive =
                            activeTab[0] === mainIdx &&
                            activeTab[1] === itemIdx &&
                            activeSubTab === subIdx;

                          // Check if user is already on this sub-route
                          const isOnCurrentSubRoute = sub.route === currentPath;

                          return (
                            <ListItemButton
                              key={sub.label}
                              selected={subIsActive}
                              component="a"
                              href={sub.route}
                              sx={{
                                pl: { xs: 4, lg: 5, xl: 6 },
                                '&:hover': {
                                  borderRadius: `${theme.borderRadius.full}px`,
                                },
                                transition: theme.transitions.custom.drawer,
                                borderRadius: subIsActive
                                  ? `${theme.borderRadius.full}px`
                                  : undefined,
                                color: subIsActive
                                  ? theme.palette.leftNavigation.activeText
                                  : theme.palette.leftNavigation.text,
                                backgroundColor: subIsActive
                                  ? `${theme.palette.leftNavigation.activeBg} !important`
                                  : '',
                              }}
                              onClick={(e: React.MouseEvent) => {
                                if (isModifiedClick(e)) return;
                                e.preventDefault();
                                if (isOnCurrentSubRoute) return; // Prevent navigation to current route
                                handleParentClick(mainIdx, subIdx, sub.route);
                              }}
                            >
                              <ListItemIcon
                                sx={{
                                  minWidth: {
                                    xs: '16px',
                                    lg: '18px',
                                    xl: '20px',
                                  },
                                }}
                              >
                                <FiberManualRecordIcon
                                  sx={{
                                    fontSize: { xs: 6, lg: 7, xl: 8 },
                                    color: subIsActive
                                      ? theme.palette.leftNavigation.activeText
                                      : theme.palette.common.black,
                                  }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                slotProps={{
                                  primary: {
                                    sx: {
                                      fontSize: {
                                        xs: '12px',
                                        lg: '13px',
                                        xl: '14px',
                                      },
                                    },
                                  },
                                }}
                                primary={sub.label}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      ))}
    </Box>
  );
};

export default React.memo(LeftNavigation);
